/**
 * HTTP-клиент.
 *
 * Тонкий слой над fetch: подставляет токен, разбирает ошибки в понятный
 * текст и отличает «сеть недоступна» от «сервер отказал». Разница
 * принципиальная: первое чинится очередью синхронизации, второе — нет.
 */

import { clearToken, getToken, setToken } from '../auth/session'

export const API_BASE = '/api'

/**
 * Заголовок, которым сервер отдаёт свежий сессионный токен.
 *
 * Контракт с бэкендом (`backend/session.py`, `SESSION_TOKEN_HEADER`) —
 * менять имя можно только вместе с ним.
 */
const SESSION_TOKEN_HEADER = 'X-Session-Token'

/** Сервер отказал: 4xx/5xx с разобранным текстом причины. */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message)
    this.name = 'ApiError'
  }

  /** Сессия мертва — нужно входить заново. */
  get isAuth(): boolean {
    return this.status === 401
  }
}

/** Запрос не дошёл: нет сети, таймаут, самолётный режим в подвале зала. */
export class NetworkError extends Error {
  constructor(message = 'Нет связи с сервером', options?: ErrorOptions) {
    super(message, options)
    this.name = 'NetworkError'
  }
}

interface RequestOptions {
  method?: string
  body?: unknown
  /** Не подставлять токен (для входа). */
  anonymous?: boolean
  signal?: AbortSignal
  timeoutMs?: number
}

/** Запас времени по умолчанию. В зале сеть бывает медленной, но не бесконечной. */
const DEFAULT_TIMEOUT_MS = 15000

async function extractError(response: Response): Promise<string> {
  try {
    const data = await response.json()
    const detail = (data as { detail?: unknown }).detail
    if (typeof detail === 'string') return detail
    if (Array.isArray(detail)) {
      // Ошибка валидации FastAPI: массив объектов с msg и loc.
      const first = detail[0] as { msg?: string; loc?: unknown[] } | undefined
      if (first?.msg) {
        const field = Array.isArray(first.loc) ? first.loc.slice(-1)[0] : undefined
        return field ? `${String(field)}: ${first.msg}` : first.msg
      }
    }
    return JSON.stringify(data).slice(0, 200)
  } catch {
    return response.statusText || `Ошибка ${response.status}`
  }
}

/** Общая часть запроса: токен, таймаут, свежий токен из ответа, разбор отказа. */
async function send(path: string, options: RequestOptions = {}): Promise<Response> {
  const { method = 'GET', body, anonymous = false, timeoutMs = DEFAULT_TIMEOUT_MS } = options

  const headers: Record<string, string> = {}
  if (body !== undefined) headers['Content-Type'] = 'application/json'
  if (!anonymous) {
    const token = getToken()
    if (token) headers['Authorization'] = `Bearer ${token}`
  }

  // Свой таймер поверх внешнего сигнала: fetch сам по себе не истекает,
  // и запрос в мёртвую сеть висел бы, пока пользователь не закроет приложение.
  const controller = new AbortController()
  const timer = window.setTimeout(() => controller.abort(), timeoutMs)
  options.signal?.addEventListener('abort', () => controller.abort(), { once: true })

  let response: Response
  try {
    response = await fetch(`${API_BASE}${path}`, {
      method,
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
      signal: controller.signal,
    })
  } catch (cause) {
    throw new NetworkError(
      controller.signal.aborted ? 'Сервер не ответил вовремя' : 'Нет связи с сервером',
      { cause },
    )
  } finally {
    window.clearTimeout(timer)
  }

  // Сервер прислал свежий токен — значит либо прежний дожил до половины
  // срока, либо запрос прошёл по куке, а хранилище браузера вычистили.
  // Второе — ровно тот случай, ради которого кука и заведена: кладём токен
  // обратно, и следующий запуск снова идёт обычным путём.
  const renewed = response.headers.get(SESSION_TOKEN_HEADER)
  if (renewed) setToken(renewed)

  if (response.status === 401 && !anonymous) {
    // Токен протух или подпись сменилась. Держать мёртвую сессию незачем:
    // на следующем запуске Mini App войдёт заново сам, молча.
    clearToken()
  }

  if (!response.ok) {
    throw new ApiError(response.status, await extractError(response))
  }
  return response
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const response = await send(path, options)
  if (response.status === 204) return undefined as T
  return (await response.json()) as T
}

/** Что сервер ответил файлом: содержимое и имя, которое он предложил. */
export interface ServerFile {
  blob: Blob
  filename: string | null
}

/**
 * Файл с сервера — PDF программы или её текст.
 *
 * Имя берётся из `Content-Disposition`: сервер знает, как назвать файл
 * (дата, программа), а клиент только передаёт его дальше — в меню
 * «Поделиться» или в загрузку. Таймаут длиннее обычного: PDF собирается на
 * сервере и весит больше любого ответа API.
 */
export async function requestFile(path: string, options: RequestOptions = {}): Promise<ServerFile> {
  const response = await send(path, { ...options, timeoutMs: options.timeoutMs ?? 30000 })
  return { blob: await response.blob(), filename: filenameOf(response.headers.get('content-disposition')) }
}

/** Имя файла из заголовка: сначала `filename*` с кириллицей, потом простое. */
function filenameOf(disposition: string | null): string | null {
  if (!disposition) return null
  const utf8 = /filename\*=UTF-8''([^;]+)/i.exec(disposition)
  if (utf8?.[1]) {
    try {
      return decodeURIComponent(utf8[1])
    } catch {
      // Испорченный заголовок — берём простое имя ниже.
    }
  }
  const plain = /filename="?([^";]+)"?/i.exec(disposition)
  return plain?.[1] ?? null
}

export const api = {
  get: <T>(path: string, options?: RequestOptions) => request<T>(path, { ...options }),
  post: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'POST', body }),
  put: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'PUT', body }),
  patch: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'PATCH', body }),
  delete: <T>(path: string, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'DELETE' }),
  file: (path: string, options?: RequestOptions) => requestFile(path, options),
}
